/**
 * Utility functions to encode/decode IDs for secure URL usage
 * This obfuscates internal IDs from being visible in URLs
 */

// Simple encoding using base64 with a prefix
const PREFIX = 's'; // Story prefix
const SEPARATOR = '_';

/**
 * Encode a numeric ID to a URL-safe string
 * @param {number|string} id - The ID to encode
 * @returns {string} Encoded ID
 */
export const encodeId = (id) => {
  if (!id) return '';
  
  // Convert to string and pad with random characters for obfuscation
  const idStr = String(id);
  const timestamp = Date.now().toString(36).slice(-4); // Last 4 chars of timestamp in base36
  const combined = `${idStr}${SEPARATOR}${timestamp}`;
  
  // Encode to base64 and replace URL-unsafe characters
  const encoded = btoa(combined)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  
  return `${PREFIX}${encoded}`;
};

/**
 * Decode a URL-safe string back to numeric ID
 * @param {string} encodedId - The encoded ID
 * @returns {number|null} Decoded ID or null if invalid
 */
export const decodeId = (encodedId) => {
  if (!encodedId || !encodedId.startsWith(PREFIX)) {
    return null;
  }
  
  try {
    // Remove prefix
    const withoutPrefix = encodedId.slice(1);
    
    // Add padding if needed and replace URL-safe characters back
    const padded = withoutPrefix + '='.repeat((4 - withoutPrefix.length % 4) % 4);
    const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
    
    // Decode from base64
    const decoded = atob(base64);
    
    // Extract ID (before separator)
    const parts = decoded.split(SEPARATOR);
    if (parts.length < 1) return null;
    
    const id = parseInt(parts[0], 10);
    return isNaN(id) ? null : id;
  } catch (error) {
    console.error('Failed to decode ID:', error);
    return null;
  }
};

/**
 * Check if a string is an encoded ID
 * @param {string} str - String to check
 * @returns {boolean}
 */
export const isEncodedId = (str) => {
  return str && typeof str === 'string' && str.startsWith(PREFIX);
};

